// src/pages/dashboard/ReceptionistSettingsPage.tsx
import { useState, useEffect, useRef, useMemo } from 'react'; // useState and useEffect are already imported
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Info, Loader2, Settings as SettingsIcon, Phone } from 'lucide-react'; 
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

import {
    settingsSchema,
    callActivationSettingsSchema, 
    aiCallingHoursSchema 
} from './receptionist-settings/settingsSchemas';
import {
    SettingsFormData,
    QAPair,
    BusinessService,
    UserSettings,
    KnowledgeFile,
    AICallingHoursData,
    CallActivationSettingsData
} from './receptionist-settings/settingsTypes';
import { aiVoiceOptions } from './receptionist-settings/settingsConstants';

import TasksScheduleSettings from './receptionist-settings/tasksScheduleSettings';
import AICharacterSettings from './receptionist-settings/AICharacterSettings';
import BusinessAndKnowledgeSettings from './receptionist-settings/BusinessAndKnowledgeSettings';
import DoNotCallSettings from './receptionist-settings/DoNotCallSettings';
import AssistantTestDock from '@/components/ui/AssistantTestDock';
import { Button } from '@/components/ui/button';

const isObject = (item: unknown): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item);
const mergeDeep = (target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> => {
    const output = { ...target };
    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach(key => {
            const sourceVal = (source as Record<string, unknown>)[key];
            const targetVal = (target as Record<string, unknown>)[key];
            if (isObject(sourceVal)) {
                if (!(key in target)) Object.assign(output, { [key]: sourceVal });
                else output[key] = mergeDeep((targetVal as Record<string, unknown>) || {}, sourceVal);
            } else {
                Object.assign(output, { [key]: sourceVal });
            }
        });
    }
    return output;
};
const ADVANCE_INSTRUCTION_SLACK_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/7132591/2vroakg/";


const defaultCallActivationSettings = callActivationSettingsSchema.parse(undefined);
const defaultAiCallingHours = aiCallingHoursSchema.parse(undefined);


export default function ReceptionistSettingsPage() {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState("character"); 
    const dirtyFieldsRef = useRef<Set<keyof SettingsFormData>>(new Set());
    const playingAudioRef = useRef<HTMLAudioElement | null>(null);
    const [currentlyPlayingVoiceId, setCurrentlyPlayingVoiceId] = useState<string | null>(null);
    const [stableFormKey, setStableFormKey] = useState<string>('initial-form-key');
    const [isTestDockOpen, setIsTestDockOpen] = useState(false);


    const {
        data: settings, 
        isLoading: settingsLoading,
        error: settingsError,
        isFetching: isSettingsFetching,
    } = useQuery<UserSettings | null>({
        queryKey: ['user-settings'],
        queryFn: async () => {
            const { data: { session }, error: sessionErr } = await supabase.auth.getSession();
            if (sessionErr || !session) { console.error("Not authenticated:", sessionErr); throw new Error("Authentication failed"); }

            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/user-settings`, { method: 'GET', headers: { Authorization: `Bearer ${session.access_token}` } });
            
            if (res.status === 404 || res.status === 406) { return null; } 
            if (res.status === 204 || res.headers.get('content-length') === '0') { return null; } 

            let responseBodyText = "";
            try {
                responseBodyText = await res.text();
                if (!responseBodyText) { return null; }
                const data = JSON.parse(responseBodyText);

                if (!res.ok) {
                    const errorMessage = data?.error || `HTTP ${res.status} ${res.statusText}`;
                    throw new Error(errorMessage);
                }
                if (data === null || (Array.isArray(data) && data.length === 1 && data[0] === null)) {
                    return null;
                }
                const resultData = Array.isArray(data) ? data[0] : data;
                if (resultData && typeof resultData !== 'object') return null;

                return resultData ? {
                    ...resultData,
                    call_activation_settings: resultData.call_activation_settings ? mergeDeep(defaultCallActivationSettings, resultData.call_activation_settings) : defaultCallActivationSettings,
                    ai_calling_hours: resultData.ai_calling_hours ? mergeDeep(defaultAiCallingHours, resultData.ai_calling_hours) : defaultAiCallingHours,
                    hipaa_enabled: resultData.hipaa_enabled ?? false,
                    ai_recording_preference: resultData.ai_recording_preference ?? true,
                    background_noise_enabled: resultData.background_noise_enabled ?? true,
                    default_appointment_duration_minutes: resultData.default_appointment_duration_minutes ?? 30,
                    call_retry_max_attempts: resultData.call_retry_max_attempts ?? 3,
                    call_retry_delay_minutes: resultData.call_retry_delay_minutes ?? 30,
                    inbound_first_message_mode: resultData.inbound_first_message_mode ?? 'assistant-speaks-first',
                    outbound_first_message_mode: resultData.outbound_first_message_mode ?? 'assistant-speaks-first-with-model-generated-message',
                    advanced_user_instructions_text: resultData.advanced_user_instructions_text ?? '',
                    voicemail_instructions_text: resultData.voicemail_instructions_text ?? '',
                } : null;
            } catch (e) {
                const message = e instanceof Error ? e.message : String(e);
                console.error("Failed to parse JSON response for user settings:", message, "Response text:", responseBodyText.substring(0,500));
                throw new Error("Failed to parse settings data");
            }
        },
        refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, retry: 1,
    });

    const isSetupComplete = useMemo(() => {
        return !!settings?.vapi_assistant_id && !!settings?.vapi_phone_number;
    }, [settings]);


    const { data: allQaPairs, isLoading: qaLoading, error: qaError } = useQuery<QAPair[]>({
        queryKey: ['business-qa-items'],
        queryFn: async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated for Q&A fetch');
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/business-qa`, { method: 'GET', headers: { Authorization: `Bearer ${session.access_token}` } });
            if (!res.ok) { const errorBody = await res.json().catch(() => ({ error: `HTTP ${res.status}`})); throw new Error(errorBody.error || `Failed to fetch Q&A (${res.status})`); }
            const data = await res.json();
            return (Array.isArray(data) ? data : []).map(item => ({ ...item, answer: String(item.answer ?? '') }));
        },
        refetchOnWindowFocus: false, enabled: settings !== undefined, retry: 1,
    });

    const { data: businessServices, isLoading: servicesLoading, error: servicesError } = useQuery<BusinessService[]>({
        queryKey: ['business-services-list'],
        queryFn: async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated for services fetch');
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/business-services`, { method: 'GET', headers: { Authorization: `Bearer ${session.access_token}` } });
            if (!res.ok) { const errorBody = await res.json().catch(() => ({ error: `HTTP ${res.status}`})); throw new Error(errorBody.error || `Failed to fetch services (${res.status})`); }
            return await res.json() as BusinessService[];
        },
        refetchOnWindowFocus: false, enabled: settings !== undefined, retry: 1,
    });

    const { data: knowledgeFiles, isLoading: filesLoading, error: filesError } = useQuery<KnowledgeFile[]>({
        queryKey: ['knowledge-files'],
        queryFn: async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Not authenticated for files fetch');
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/knowledge-files`, { method: 'GET', headers: { Authorization: `Bearer ${session.access_token}` } });
            if (!res.ok) { const errorBody = await res.json().catch(() => ({ error: `HTTP ${res.status}`})); throw new Error(errorBody.error || `Failed to fetch files (${res.status})`); }
            return await res.json() as KnowledgeFile[];
        },
        refetchOnWindowFocus: false, enabled: settings !== undefined, retry: 1,
    });

    const {
        register: registerSettings,
        handleSubmit: handleSettingsSubmit,
        formState: { errors: settingsErrors, isDirty: isSettingsDirty, dirtyFields },
        reset: resetSettingsForm,
        watch,
        control,
        setValue: setSettingsValue,
        getValues, 
        trigger, 
    } = useForm<SettingsFormData>({
        resolver: zodResolver(settingsSchema),
        mode: 'onBlur', 
        defaultValues: {
            ai_greeting_message: '', 
            ai_forwarding_number: '',
            reply_to_email: '',
            ai_voice_id: aiVoiceOptions[0].id, 
            ai_voice_provider: aiVoiceOptions[0].provider,
            ai_name: aiVoiceOptions[0].name,
            business_name: '',
            call_activation_settings: defaultCallActivationSettings,
            ai_calling_hours: defaultAiCallingHours,
            hipaa_enabled: false,
            ai_recording_preference: true,
            background_noise_enabled: true,
            business_address: '',
            business_hours_raw: '',
            default_appointment_duration_minutes: 30,
            call_retry_max_attempts: 3,
            call_retry_delay_minutes: 30,
            inbound_first_message_mode: 'assistant-speaks-first',
            outbound_first_message_mode: 'assistant-speaks-first-with-model-generated-message',
            advanced_user_instructions_text: '',
            voicemail_instructions_text: '',
        },
        resetOptions: { keepDirtyValues: false }
    });

    const formValues = watch();


    useEffect(() => {
        if (settings !== undefined) { 
            const defaultVoice = aiVoiceOptions[0];
            const voiceIdToUse = settings?.ai_voice_id ?? getValues("ai_voice_id") ?? defaultVoice.id;
            const voiceProviderToUse = settings?.ai_voice_provider ?? getValues("ai_voice_provider") ?? defaultVoice.provider;
            const aiNameToUse = settings?.ai_name ?? getValues("ai_name") ?? defaultVoice.name;
            
            const mergedCAS: CallActivationSettingsData = settings?.call_activation_settings
                ? (mergeDeep(
                    defaultCallActivationSettings as unknown as Record<string, unknown>,
                    settings.call_activation_settings as unknown as Record<string, unknown>
                  ) as unknown as CallActivationSettingsData)
                : getValues("call_activation_settings");
            const mergedHoursData: AICallingHoursData = settings?.ai_calling_hours
                ? (mergeDeep(
                    defaultAiCallingHours as unknown as Record<string, unknown>,
                    settings.ai_calling_hours as unknown as Record<string, unknown>
                  ) as unknown as AICallingHoursData)
                : getValues("ai_calling_hours");
           
            const mergedHours: AICallingHoursData = {
                timezone: mergedHoursData.timezone || defaultAiCallingHours.timezone,
                days: { ...defaultAiCallingHours.days, ...mergedHoursData.days }
            };


            resetSettingsForm({
                ai_greeting_message: settings?.ai_greeting_message ?? getValues("ai_greeting_message") ?? '',
                ai_forwarding_number: settings?.ai_forwarding_number ?? getValues("ai_forwarding_number") ?? '',
                reply_to_email: settings?.reply_to_email ?? getValues("reply_to_email") ?? '',
                ai_voice_id: voiceIdToUse,
                ai_voice_provider: voiceProviderToUse,
                ai_name: aiNameToUse,
                business_name: settings?.business_name ?? getValues("business_name") ?? '',
                call_activation_settings: mergedCAS,
                ai_calling_hours: mergedHours,
                hipaa_enabled: settings?.hipaa_enabled ?? getValues("hipaa_enabled") ?? false,
                ai_recording_preference: settings?.ai_recording_preference ?? getValues("ai_recording_preference") ?? true,
                background_noise_enabled: settings?.background_noise_enabled ?? getValues("background_noise_enabled") ?? true,
                business_address: settings?.business_address ?? getValues("business_address") ?? '',
                business_hours_raw: settings?.business_hours_raw ?? getValues("business_hours_raw") ?? '',
                default_appointment_duration_minutes: settings?.default_appointment_duration_minutes ?? getValues("default_appointment_duration_minutes") ?? 30,
                call_retry_max_attempts: settings?.call_retry_max_attempts ?? getValues("call_retry_max_attempts") ?? 3,
                call_retry_delay_minutes: settings?.call_retry_delay_minutes ?? getValues("call_retry_delay_minutes") ?? 30,
                inbound_first_message_mode: settings?.inbound_first_message_mode ?? getValues("inbound_first_message_mode") ?? 'assistant-speaks-first',
                outbound_first_message_mode: settings?.outbound_first_message_mode ?? getValues("outbound_first_message_mode") ?? 'assistant-speaks-first-with-model-generated-message',
                advanced_user_instructions_text: settings?.advanced_user_instructions_text ?? getValues("advanced_user_instructions_text") ?? '',
                voicemail_instructions_text: settings?.voicemail_instructions_text ?? getValues("voicemail_instructions_text") ?? '',
            }, { keepValues: false, keepDirty: false, keepErrors: false });
        }
    }, [settings, resetSettingsForm, getValues]); 
    
    useEffect(() => {
        if (settings?.updated_at) {
            setStableFormKey(settings.updated_at);
        }
    }, [settings?.updated_at]);

    const configureVapiMutation = useMutation({
        mutationFn: async (data: { 
            greeting?: string | null; 
            forwarding_number?: string | null; 
            voice_id?: string | null; 
            voice_provider?: string | null; 
            ai_name?: string | null;
            business_name?: string | null;
            hipaa_enabled?: boolean;
            background_noise_enabled?: boolean;
        }) => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error("Not authenticated for VAPI config");
            if (!data.greeting || !data.voice_id || !data.voice_provider || data.ai_name === undefined || data.business_name === undefined || data.hipaa_enabled === undefined) {
                throw new Error("Cannot configure AI: Essential data for VAPI endpoint missing.");
            }
            const payload = { 
                greeting: data.greeting, 
                forwarding_number: data.forwarding_number || '', 
                voice_id: data.voice_id, 
                voice_provider: data.voice_provider, 
                ai_name: data.ai_name,
                business_name: data.business_name,
                hipaa_enabled: data.hipaa_enabled,
                background_noise_enabled: data.background_noise_enabled,
            };
            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vapi-assistant-config`, { method: 'PUT', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const resBody = await res.json().catch(() => { throw new Error(`Failed to parse VAPI config response (Status: ${res.status})`) });
            if (!res.ok) throw new Error(resBody.error || `HTTP ${res.status} error configuring VAPI assistant`);
            return resBody;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['user-settings'] });
            queryClient.invalidateQueries({ queryKey: ['assistant-id'] });
            queryClient.invalidateQueries({ queryKey: ['business-qa-items'] });
            toast.success('✅ Your AI is ready to take calls!');
        },
        onError: (err: Error) => {
             let displayError = err.message;
             if (err.message.includes("Failed to load prompt file") || err.message.includes("Cannot find module")) {
                 displayError = "Failed to load necessary AI configuration files. Please try saving again or contact support.";
             } else if (err.message.includes("VAPI API Error")) {
                 displayError = `Could not sync with VAPI: ${err.message}. Check connection or VAPI status.`;
             }
            toast.error(`Failed to sync AI configuration: ${displayError}`);
        },
    });

    const triggerVapiConfigUpdateFromChild = async () => {
        console.log("Attempting to trigger VAPI config update from child component change...");
        const isValid = await trigger(); 
        if (!isValid) {
            toast.error("Please fix the errors first.");
            const fieldErrors = settingsErrors; 
            if (fieldErrors.call_activation_settings || fieldErrors.ai_calling_hours) setActiveTab("tasks");
            else if (fieldErrors.ai_greeting_message || fieldErrors.ai_voice_id || fieldErrors.ai_forwarding_number || fieldErrors.reply_to_email) setActiveTab("character");
            else if (fieldErrors.business_name || fieldErrors.advanced_user_instructions_text) setActiveTab("business");
            return;
        }

        const currentSettingsValues = getValues(); 
        const { ai_greeting_message, ai_forwarding_number, ai_voice_id, ai_voice_provider, ai_name, business_name, hipaa_enabled, ai_recording_preference, background_noise_enabled } = currentSettingsValues;

        const canTriggerVapiConfig = !!ai_greeting_message && !!ai_voice_id && !!ai_voice_provider && ai_name !== undefined && business_name !== undefined && hipaa_enabled !== undefined && ai_recording_preference !== undefined && background_noise_enabled !== undefined;

        if (canTriggerVapiConfig && !configureVapiMutation.isPending) {
            toast.info("Updating your AI...");
            configureVapiMutation.mutate({
                greeting: ai_greeting_message, 
                forwarding_number: ai_forwarding_number, 
                voice_id: ai_voice_id, 
                voice_provider: ai_voice_provider,
                ai_name, 
                business_name, 
                hipaa_enabled: hipaa_enabled ?? false,
                background_noise_enabled: background_noise_enabled ?? true
            });
        } else if (!canTriggerVapiConfig) {
            toast.warning("AI needs your business name and greeting to start working. Please complete those sections.", { duration: 7000 });
        } else {
            toast.info("AI sync is already in progress.");
        }
    };


    const updateSettingsMutation = useMutation({
        mutationFn: async (formData: SettingsFormData) => { 
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error("Not authenticated");

            const payloadToSend: Partial<SettingsFormData> = {};
            (Object.keys(formData) as Array<keyof SettingsFormData>).forEach(key => {
                const value = formData[key];
                const keyStr = String(key);
                if (value === '') {
                    if (['ai_forwarding_number', 'reply_to_email', 'ai_name', 'business_name', 'business_address', 'business_hours_raw', 'advanced_user_instructions_text', 'voicemail_instructions_text'].includes(keyStr)) {
                        payloadToSend[keyStr as keyof SettingsFormData] = null as any; 
                    } else {
                        (payloadToSend as Record<string, unknown>)[keyStr] = value; 
                    }
                } else if (value !== undefined) { 
                    (payloadToSend as Record<string, unknown>)[keyStr] = value;
                }
            });
            if (formData.default_appointment_duration_minutes === undefined || formData.default_appointment_duration_minutes === null || isNaN(Number(formData.default_appointment_duration_minutes))) {
                payloadToSend.default_appointment_duration_minutes = null;
            } else {
                payloadToSend.default_appointment_duration_minutes = Number(formData.default_appointment_duration_minutes);
            }

            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/user-settings`, {
                method: 'PUT', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payloadToSend)
            });
            const resBody = await res.json().catch(() => { throw new Error(`Failed to parse settings save response (Status: ${res.status})`) });
            if (!res.ok) throw new Error(resBody.error || `HTTP ${res.status} error saving settings`);
            return resBody as UserSettings;
        },
        onSuccess: (savedData) => {
            toast.success('Settings saved successfully!');
            queryClient.setQueryData(['user-settings'], (old: UserSettings | null | undefined) => savedData ? { ...(old ?? {} as UserSettings), ...savedData } : old);

            const previouslyDirtyFieldsSet = dirtyFieldsRef.current;
            const vapiConfigRelevantFields: ReadonlyArray<keyof SettingsFormData> = [
                'ai_greeting_message', 'ai_forwarding_number', 'reply_to_email',
                'ai_voice_id', 'ai_voice_provider', 'ai_name', 'business_name',
                'business_address', 'business_hours_raw',
                'hipaa_enabled', 'ai_recording_preference', 'background_noise_enabled',
                'default_appointment_duration_minutes', 'ai_calling_hours',
                'advanced_user_instructions_text', 'voicemail_instructions_text',
                'inbound_first_message_mode', 'outbound_first_message_mode'
            ];
            
            const vapiConfigRelevantFieldsChanged = vapiConfigRelevantFields.some(field => 
                previouslyDirtyFieldsSet.has(field)
            );

            const { ai_greeting_message, ai_forwarding_number, ai_voice_id, ai_voice_provider, ai_name, business_name, hipaa_enabled, ai_recording_preference, background_noise_enabled } = savedData || {};
            const canTriggerVapiConfig = !!ai_greeting_message && !!ai_voice_id && !!ai_voice_provider && ai_name !== undefined && business_name !== undefined && hipaa_enabled !== undefined && ai_recording_preference !== undefined && background_noise_enabled !== undefined;

            if (vapiConfigRelevantFieldsChanged && canTriggerVapiConfig && !configureVapiMutation.isPending) {
                configureVapiMutation.mutate({
                    greeting: ai_greeting_message, 
                    forwarding_number: ai_forwarding_number, 
                    voice_id: ai_voice_id, 
                    voice_provider: ai_voice_provider,
                    ai_name, 
                    business_name, 
                    hipaa_enabled: hipaa_enabled ?? false,
                    background_noise_enabled: background_noise_enabled ?? true
                });
            } else if (vapiConfigRelevantFieldsChanged && !canTriggerVapiConfig) {
                toast.warning("Settings saved, but AI needs your business name and greeting to start working.", { duration: 7000 });
            }
            dirtyFieldsRef.current.clear();
        },
        onError: (err: Error) => { toast.error(`Failed to save settings: ${err.message}`); },
    });


    const onSettingsSubmit = async (data: SettingsFormData) => { 
        const isValid = await trigger(); 
        if (!isValid) {
            toast.error("Please fix the errors before saving.");
            const fieldErrors = settingsErrors; 
            if (fieldErrors.call_activation_settings || fieldErrors.ai_calling_hours) setActiveTab("tasks");
            else if (fieldErrors.ai_greeting_message || fieldErrors.ai_voice_id || fieldErrors.ai_forwarding_number || fieldErrors.reply_to_email) setActiveTab("character");
            else if (fieldErrors.business_name || fieldErrors.advanced_user_instructions_text || fieldErrors.business_address || fieldErrors.business_hours_raw || fieldErrors.default_appointment_duration_minutes) setActiveTab("business");
            return;
        }

        const currentDirtyFields = new Set<keyof SettingsFormData>();
        for (const key of Object.keys(dirtyFields) as Array<keyof SettingsFormData>) {
            if (dirtyFields[key]) {
                currentDirtyFields.add(key);
            }
        }
        dirtyFieldsRef.current = currentDirtyFields;
        const { data: { user } } = await supabase.auth.getUser();

        updateSettingsMutation.mutate(data);
        
        if (currentDirtyFields.has('advanced_user_instructions_text')) {
        try {
            const payload = new URLSearchParams({
                user_email: user?.email ?? '',
                instruction: data.advanced_user_instructions_text || '',
                business_name: data.business_name || '',
                timestamp: new Date().toISOString(),
            });

            await fetch(ADVANCE_INSTRUCTION_SLACK_WEBHOOK_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: payload.toString(),
            });

            console.log('Instruction sent to Zapier successfully');
        } catch (zapErr) {
            console.error('Failed to send instruction to Zapier:', zapErr);
        }
    }



    };
    
    const handlePlaySample = (sampleUrl?: string | null, voiceId?: string) => {
        if (playingAudioRef.current && currentlyPlayingVoiceId !== voiceId) {
            playingAudioRef.current.pause();
            playingAudioRef.current.currentTime = 0;
        }
        if (!sampleUrl) { 
            toast.error("No audio sample available for this voice."); 
            setCurrentlyPlayingVoiceId(null);
            return; 
        }
        try {
            const audio = new Audio(sampleUrl); 
            playingAudioRef.current = audio;
            audio.play().catch(err => { 
                console.error("Error playing audio:", err); 
                toast.error(`Could not play audio: ${err.message}`); 
                playingAudioRef.current = null; 
                setCurrentlyPlayingVoiceId(null);
            });
            setCurrentlyPlayingVoiceId(voiceId || null);
            audio.onended = () => { 
                playingAudioRef.current = null; 
                setCurrentlyPlayingVoiceId(null);
            };
            audio.onpause = () => { 
                if (playingAudioRef.current && playingAudioRef.current.currentTime !== playingAudioRef.current.duration) {
                    // Keep currentlyPlayingVoiceId if paused manually for the same voice
                } else {
                    setCurrentlyPlayingVoiceId(null);
                }
            };
        } catch (err: unknown) { 
            const message = err instanceof Error ? err.message : String(err);
            console.error("Error creating Audio object:", message); 
            toast.error(`Failed to load audio sample: ${message}`); 
            playingAudioRef.current = null; 
            setCurrentlyPlayingVoiceId(null);
        }
    };

    const copyToClipboard = (text: string | null | undefined) => {
        if (text) { navigator.clipboard.writeText(text).then(() => toast.success('Phone number copied!')).catch(() => toast.error('Failed to copy number.')); }
        else { toast.error('No phone number assigned yet.'); }
    };

    const isPageLoading = settingsLoading && settings === undefined;
    const isSavingSettings = updateSettingsMutation.isPending;
    const isSyncingVapi = configureVapiMutation.isPending;
    const isSaving = isSavingSettings || isSyncingVapi;


    return (
        <div className="space-y-6 p-4 md:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-gray flex items-center">
                    <SettingsIcon className="h-8 w-8 mr-3 text-indigo-600" />
                    Your AI Employee Setup
                </h1>
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        onClick={() => setIsTestDockOpen(true)}
                        aria-label="Hear Your AI Live"
                        className="bg-indigo-600 hover:bg-indigo-700"
                        size="sm"
                    >
                        <Phone className="h-4 w-4 mr-2" /> Hear Your AI Live
                    </Button>
                </div>
            </div>


            {isPageLoading && ( <div className="flex justify-center items-center h-64"><Loader2 className="h-8 w-8 animate-spin text-hero-blue mr-3" /> Getting your AI ready...</div> )}
            {!isPageLoading && settingsError && ( <div className="text-red-600 p-4 border border-red-300 bg-red-50 rounded">Error loading settings: {(settingsError as Error).message}. Please try refreshing.</div> )}
            
            {!isPageLoading && !settingsError && !isSetupComplete && settings !== undefined && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded relative mb-6" role="alert">
                    <strong className="font-bold mr-2"><Info className="inline w-4 h-4 mr-1 -mt-1" />Almost Ready:</strong>
                    <span className="block sm:inline ml-1"> Complete the sections below to get your AI phone number.</span>
                </div>
            )}

            {!isPageLoading && !settingsError && (
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 mb-6">
                        <TabsTrigger value="tasks">When AI Works</TabsTrigger>
                        <TabsTrigger value="character">AI Voice & Style</TabsTrigger>
                        <TabsTrigger value="business">Your Business</TabsTrigger>
                        <TabsTrigger value="knowledge" disabled={!isSetupComplete}>What AI Knows</TabsTrigger>
                        <TabsTrigger value="dnc" disabled={!isSetupComplete}>Do Not Call</TabsTrigger>
                    </TabsList>

                    <form onSubmit={handleSettingsSubmit(onSettingsSubmit)} key={stableFormKey}>
                        <TabsContent value="tasks">
                            <div>
                                <TasksScheduleSettings
                                    control={control}
                                    errors={settingsErrors}
                                    watch={watch}
                                    setValue={setSettingsValue}
                                    isSettingsDirty={isSettingsDirty}
                                    isSaving={isSaving}
                                    isSettingsFetching={isSettingsFetching}
                                />
                            </div>
                        </TabsContent>

                        <TabsContent value="character">
                            <div>
                                <AICharacterSettings
                                    control={control}
                                    errors={settingsErrors}
                                    register={registerSettings}
                                    setValue={setSettingsValue}
                                    handlePlaySample={handlePlaySample}
                                    playingAudioRef={playingAudioRef}
                                    isSettingsDirty={isSettingsDirty}
                                    isSaving={isSaving}
                                    isSettingsFetching={isSettingsFetching}
                                    isSetupComplete={isSetupComplete}
                                    currentSettings={formValues as SettingsFormData | undefined}
                                />
                            </div>
                        </TabsContent>

                        <TabsContent value="business">
                            <div>
                                <BusinessAndKnowledgeSettings
                                    mainFormControl={control}
                                    mainFormErrors={settingsErrors}
                                    mainFormRegister={registerSettings}
                                    mainFormSetValue={setSettingsValue}
                                    settings={settings}
                                    isSettingsFetching={isSettingsFetching}
                                    copyToClipboard={copyToClipboard}
                                    isSetupComplete={isSetupComplete}
                                    allQaPairs={allQaPairs}
                                    qaLoading={qaLoading}
                                    qaError={qaError as Error | null}
                                    businessServices={businessServices}
                                    servicesLoading={servicesLoading}
                                    servicesError={servicesError as Error | null}
                                    knowledgeFiles={knowledgeFiles}
                                    filesLoading={filesLoading}
                                    filesError={filesError as Error | null}
                                    isMainFormDirty={isSettingsDirty}
                                    isSavingOrSyncing={isSaving}
                                    activeTab="business"
                                    triggerVapiConfigUpdate={triggerVapiConfigUpdateFromChild}
                                />
                            </div>
                        </TabsContent>

                        <TabsContent value="knowledge">
                            <div>
                                <BusinessAndKnowledgeSettings
                                    mainFormControl={control}
                                    mainFormErrors={settingsErrors}
                                    mainFormRegister={registerSettings}
                                    mainFormSetValue={setSettingsValue}
                                    settings={settings}
                                    isSettingsFetching={isSettingsFetching}
                                    copyToClipboard={copyToClipboard}
                                    isSetupComplete={isSetupComplete}
                                    allQaPairs={allQaPairs}
                                    qaLoading={qaLoading}
                                    qaError={qaError as Error | null}
                                    businessServices={businessServices}
                                    servicesLoading={servicesLoading}
                                    servicesError={servicesError as Error | null}
                                    knowledgeFiles={knowledgeFiles}
                                    filesLoading={filesLoading}
                                    filesError={filesError as Error | null}
                                    isMainFormDirty={isSettingsDirty}
                                    isSavingOrSyncing={isSaving}
                                    activeTab="knowledge"
                                    triggerVapiConfigUpdate={triggerVapiConfigUpdateFromChild}
                                />
                            </div>
                        </TabsContent>

                        <TabsContent value="dnc">
                            <div>
                                <DoNotCallSettings />
                            </div>
                        </TabsContent>
                    </form>
                </Tabs>
            )}

            <AssistantTestDock
                assistantId={settings?.vapi_assistant_id ?? null}
                open={isTestDockOpen}
                onOpenChange={setIsTestDockOpen}
            />
        </div>
    );
}